import React from 'react';

// Utils
import { FormattedMessage } from '../../util/reactIntl';
import { richText } from '../../util/richText';
import {
  getFieldValue,
  getDetailCustomFieldValue,
  isFieldForCategory,
  isFieldForListingType,
  pickCategoryFields,
  pickCustomFieldProps,
} from '../../util/fieldHelpers.js';
import {
  CAREGIVER_SEARCH_TEXT_FIELD_KEY,
  getOptionLabel,
  getOptionsForField,
  parseServiceRadiusLabel,
} from '../../util/caregiverSearch.js';

// Shared components
import { Heading, PropertyGroup } from '../../components';

// Related components and modules
import CustomExtendedDataSection from '../../components/CustomExtendedDataSection/CustomExtendedDataSection.js';

import css from './ListingPage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 20;

const CAREGIVER_FIELD_KEYS = new Set([
  'category',
  'serviceCategories',
  'serviceGroups',
  'services',
  'experience',
  'schedulePreference',
  'serviceRadius',
  'cert_training',
  'transportation_req',
  'languages',
  'other_languages',
  'service_notes',
  'experience_level',
]);

const GROUPED_CAREGIVER_KEYS = new Set([
  'category',
  'serviceCategories',
  'serviceGroups',
  'services',
  'experience',
  'schedulePreference',
  'serviceRadius',
  'cert_training',
  'transportation_req',
  'languages',
  'other_languages',
  'service_notes',
  'experience_level',
]);

const caregiverLabelOverrides = intl => ({
  schedulePreference: intl.formatMessage({ id: 'ListingPage.schedulePreferenceShortLabel' }),
});

const caregiverValueOverrides = intl => ({
  schedulePreference: {
    daytime_hours: intl.formatMessage({ id: 'ListingPage.schedulePreferenceValue.daytime_hours' }),
  },
  availabilityType: {
    daytime_hours: intl.formatMessage({ id: 'ListingPage.schedulePreferenceValue.daytime_hours' }),
  },
});

const shouldHideCaregiverFallbackField = config => {
  const key = config?.key;
  const label = config?.showConfig?.label || '';

  return (
    key === CAREGIVER_SEARCH_TEXT_FIELD_KEY ||
    key === 'category' ||
    key === 'serviceCategories' ||
    key === 'serviceGroups' ||
    key === 'schedulePreference' ||
    label === 'Category' ||
    label === 'Scheduling Preference & Availability Type'
  );
};

const createFilterOptions = options =>
  options.map(option => ({ key: option.value, label: option.label }));

const renderRichText = text =>
  richText(text, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
    breakChars: '/',
  });

const renderDetailList = details =>
  details?.length > 0 ? (
    <ul className={css.details}>
      {details.map(detail => (
        <li key={detail.key} className={css.detailsRow}>
          <span className={css.detailLabel}>{detail.label}</span>
          <span>{detail.value}</span>
        </li>
      ))}
    </ul>
  ) : null;

const renderPropertyBlock = ({
  heading,
  idPrefix,
  idSuffix,
  selectedOptions,
  options,
  showUnselectedOptions = false,
}) => {
  if (!selectedOptions?.length && !showUnselectedOptions) {
    return null;
  }

  return (
    <div key={idSuffix} className={css.detailsGroupBlock}>
      <h3 className={css.detailsGroupHeading}>{heading}</h3>
      <PropertyGroup
        id={`${idPrefix}.${idSuffix}`}
        ariaLabel={heading}
        options={options}
        selectedOptions={selectedOptions}
        twoColumns={options.length > 5}
        showUnselectedOptions={showUnselectedOptions}
      />
    </div>
  );
};

const renderTextBlock = ({ heading, text, key, rootClassName }) => {
  if (!text) {
    return null;
  }

  return (
    <div key={key} className={[css.detailsGroupBlock, rootClassName].filter(Boolean).join(' ')}>
      <h3 className={css.detailsGroupHeading}>{heading}</h3>
      <p className={css.clusterText}>{renderRichText(text)}</p>
    </div>
  );
};

const getExtendedDataValueForConfig = (config, publicData, metadata) => {
  if (!config?.key) {
    return null;
  }

  const scope = config.scope || 'public';
  return scope === 'metadata'
    ? getFieldValue(metadata, config.key)
    : getFieldValue(publicData, config.key);
};

const CustomListingFields = props => {
  const { publicData, metadata, listingFieldConfigs, categoryConfiguration, intl } = props;

  const { key: categoryPrefix, categories: listingCategoriesConfig } = categoryConfiguration;
  const categoriesObj = pickCategoryFields(publicData, categoryPrefix, 1, listingCategoriesConfig);
  const currentCategories = Object.values(categoriesObj);

  const isFieldForSelectedCategories = fieldConfig => {
    const isTargetCategory = isFieldForCategory(currentCategories, fieldConfig);
    return isTargetCategory;
  };

  const allFieldConfigs = listingFieldConfigs || [];
  const caregiverFieldConfigs = allFieldConfigs.filter(config =>
    CAREGIVER_FIELD_KEYS.has(config.key)
  );
  const fallbackFieldConfigs = allFieldConfigs.filter(
    config => !GROUPED_CAREGIVER_KEYS.has(config.key) && !shouldHideCaregiverFallbackField(config)
  );

  const labelOverrides = caregiverLabelOverrides(intl);
  const valueOverrides = caregiverValueOverrides(intl);
  const getFieldConfig = key => caregiverFieldConfigs.find(config => config.key === key);
  const getConfiguredFieldByLabel = label =>
    allFieldConfigs.find(config => config?.showConfig?.label === label);
  const getFieldLabel = key =>
    labelOverrides[key] || getFieldConfig(key)?.showConfig?.label || getOptionLabel(key, key);
  const getFieldValueLabel = (key, value) => {
    if (typeof value === 'string' && valueOverrides[key]?.[value]) {
      return valueOverrides[key][value];
    }

    return getOptionLabel(key, value, getFieldConfig(key));
  };
  const getMultiEnumSelected = key => (Array.isArray(publicData[key]) ? publicData[key] : []);
  const getMultiEnumOptions = key =>
    createFilterOptions(getOptionsForField(key, getFieldConfig(key)));

  const isCaregiverListing = caregiverFieldConfigs.length > 0;

  const propsForCustomFields =
    pickCustomFieldProps(
      { publicData, metadata },
      fallbackFieldConfigs,
      'listingType',
      isFieldForSelectedCategories
    ) || [];

  const fallbackSectionDetailsProps = {
    ...props,
    isFieldForCategory: isFieldForSelectedCategories,
    fieldConfigs: fallbackFieldConfigs,
    heading: 'ListingPage.detailsTitle',
    rootClassName: css.fallbackExtendedSection,
  };

  const pickExtendedDataFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForSelectedCategories(config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ListingPage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };

  const experienceDetails = [];
  const experienceLevelValue = publicData.experience_level;
  if (experienceLevelValue) {
    experienceDetails.push({
      key: 'experience_level',
      label: getFieldLabel('experience_level'),
      value: getFieldValueLabel('experience_level', experienceLevelValue),
    });
  }

  const practicalDetails = [];
  const schedulePreferenceConfig =
    getFieldConfig('schedulePreference') ||
    getConfiguredFieldByLabel('Scheduling Preference & Availability Type');
  const schedulePreferenceKey = schedulePreferenceConfig?.key || 'schedulePreference';
  const schedulePreferenceValue = getExtendedDataValueForConfig(
    schedulePreferenceConfig,
    publicData,
    metadata
  );
  if (schedulePreferenceValue) {
    practicalDetails.push({
      key: schedulePreferenceKey,
      label:
        labelOverrides.schedulePreference ||
        schedulePreferenceConfig?.showConfig?.label ||
        intl.formatMessage({ id: 'ListingPage.schedulePreferenceShortLabel' }),
      value: getFieldValueLabel(schedulePreferenceKey, schedulePreferenceValue),
    });
  }

  const serviceRadiusValue = parseServiceRadiusLabel(publicData.serviceRadius);
  if (serviceRadiusValue) {
    practicalDetails.push({
      key: 'serviceRadius',
      label: getFieldLabel('serviceRadius'),
      value: serviceRadiusValue,
    });
  }

  const servicesSection = renderPropertyBlock({
    heading: getFieldLabel('services'),
    idPrefix: 'listingPage',
    idSuffix: 'services',
    selectedOptions: getMultiEnumSelected('services'),
    options: getMultiEnumOptions('services'),
  });

  const serviceCategoriesSection = renderPropertyBlock({
    heading:
      getFieldConfig('serviceCategories')?.showConfig?.label ||
      intl.formatMessage({
        id: 'ListingPage.serviceCategoriesTitle',
        defaultMessage: 'Service categories',
      }),
    idPrefix: 'listingPage',
    idSuffix: 'serviceCategories',
    selectedOptions: getMultiEnumSelected('serviceCategories'),
    options: getMultiEnumOptions('serviceCategories'),
  });

  const experienceBlocks = [
    renderPropertyBlock({
      heading: getFieldLabel('experience'),
      idPrefix: 'listingPage',
      idSuffix: 'experience',
      selectedOptions: getMultiEnumSelected('experience'),
      options: getMultiEnumOptions('experience'),
    }),
    renderPropertyBlock({
      heading: getFieldLabel('cert_training'),
      idPrefix: 'listingPage',
      idSuffix: 'cert_training',
      selectedOptions: getMultiEnumSelected('cert_training'),
      options: getMultiEnumOptions('cert_training'),
    }),
  ].filter(Boolean);

  const practicalBlocks = [
    renderPropertyBlock({
      heading: getFieldLabel('transportation_req'),
      idPrefix: 'listingPage',
      idSuffix: 'transportation_req',
      selectedOptions: getMultiEnumSelected('transportation_req'),
      options: getMultiEnumOptions('transportation_req'),
    }),
    renderPropertyBlock({
      heading: getFieldLabel('languages'),
      idPrefix: 'listingPage',
      idSuffix: 'languages',
      selectedOptions: getMultiEnumSelected('languages'),
      options: getMultiEnumOptions('languages'),
    }),
    renderTextBlock({
      heading: getFieldLabel('other_languages'),
      text: publicData.other_languages,
      key: 'other_languages',
    }),
    renderTextBlock({
      heading: getFieldLabel('service_notes'),
      text: publicData.service_notes,
      key: 'service_notes',
      rootClassName: css.supportNotesBlock,
    }),
  ].filter(Boolean);

  const hasCaregiverGroups =
    !!servicesSection ||
    experienceDetails.length > 0 ||
    experienceBlocks.length > 0 ||
    practicalDetails.length > 0 ||
    practicalBlocks.length > 0;

  return (
    <>
      {isCaregiverListing && hasCaregiverGroups ? (
        <div className={css.detailsClusters}>
          {servicesSection ? (
            <section className={css.detailsCluster}>
              <Heading as="h2" rootClassName={css.detailsClusterTitle}>
                <FormattedMessage id="ListingPage.servicesOfferedTitle" />
              </Heading>
              {serviceCategoriesSection}
              {servicesSection}
            </section>
          ) : null}

          {experienceDetails.length > 0 || experienceBlocks.length > 0 ? (
            <section className={css.detailsCluster}>
              <Heading as="h2" rootClassName={css.detailsClusterTitle}>
                <FormattedMessage id="ListingPage.experienceSectionTitle" />
              </Heading>
              {renderDetailList(experienceDetails)}
              {experienceBlocks}
            </section>
          ) : null}

          {practicalDetails.length > 0 || practicalBlocks.length > 0 ? (
            <section className={css.detailsCluster}>
              <Heading as="h2" rootClassName={css.detailsClusterTitle}>
                <FormattedMessage id="ListingPage.practicalDetailsTitle" />
              </Heading>
              {renderDetailList(practicalDetails)}
              {practicalBlocks}
            </section>
          ) : null}
        </div>
      ) : null}

      <CustomExtendedDataSection
        sectionDetailsProps={fallbackSectionDetailsProps}
        propsForCustomFields={propsForCustomFields}
        idPrefix="listingPage"
        pickExtendedDataFields={pickExtendedDataFields}
        rootClassName={css.fallbackExtendedSection}
      />
    </>
  );
};

export default CustomListingFields;
